import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Briefcase, Trash2, Download, MapPin, RefreshCw, Tag, Check, X, Calendar } from "lucide-react";
import { API, authConfig, formatCurrency } from "./constants";

// Date range presets — values are functions returning { start, end } ISO strings, or null for "all"
function getNzFyBounds(reference = new Date()) {
  const m = reference.getMonth();
  const y = reference.getFullYear();
  const startYear = m >= 3 ? y : y - 1; // Apr=3 (0-indexed)
  const start = new Date(Date.UTC(startYear, 3, 1, 0, 0, 0));
  const end = new Date(Date.UTC(startYear + 1, 2, 31, 23, 59, 59));
  const label = `${String(startYear).slice(-2)}/${String(startYear + 1).slice(-2)}`;
  return { start, end, label };
}

const RANGE_PRESETS = [
  { key: "all", label: "All" },
  { key: "month", label: "This Month" },
  { key: "fy", label: "This FY" },
  { key: "last_fy", label: "Last FY" },
  { key: "custom", label: "Custom" },
];

function computeRange(preset, customFrom, customTo) {
  const now = new Date();
  if (preset === "all") return { start: null, end: null };
  if (preset === "month") {
    const start = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1));
    const end = new Date(Date.UTC(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59));
    return { start, end };
  }
  if (preset === "fy") {
    const { start, end } = getNzFyBounds(now);
    return { start, end };
  }
  if (preset === "last_fy") {
    const lastYear = new Date(now);
    lastYear.setFullYear(lastYear.getFullYear() - 1);
    const { start, end } = getNzFyBounds(lastYear);
    return { start, end };
  }
  if (preset === "custom") {
    return {
      start: customFrom ? new Date(customFrom + "T00:00:00Z") : null,
      end: customTo ? new Date(customTo + "T23:59:59Z") : null,
    };
  }
  return { start: null, end: null };
}

export default function MileageHistoryPanel({ user, refreshKey }) {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [rangePreset, setRangePreset] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const fetchTrips = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/mileage-trips`, authConfig());
      setTrips(resp.data || []);
    } catch (e) {
      setTrips([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchTrips(); }, [fetchTrips, refreshKey]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this work trip?")) return;
    try {
      await axios.delete(`${API}/mileage-trips/${id}`, authConfig());
      setTrips(trips.filter(t => t.id !== id));
      toast.success("Trip deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const startEdit = (t) => {
    setEditingId(t.id);
    setEditLabel(t.label || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditLabel("");
  };

  const saveEdit = async (id) => {
    try {
      const resp = await axios.patch(`${API}/mileage-trips/${id}`, { label: editLabel.trim() }, authConfig());
      setTrips(trips.map(t => t.id === id ? resp.data : t));
      setEditingId(null);
      toast.success("Label updated");
    } catch {
      toast.error("Could not update label");
    }
  };

  const handleExportCsv = () => {
    const { start, end } = computeRange(rangePreset, customFrom, customTo);
    const params = new URLSearchParams();
    if (start) params.set("start_date", start.toISOString());
    if (end) params.set("end_date", end.toISOString());
    const url = `${API}/mileage-trips/export/csv${params.toString() ? "?" + params.toString() : ""}`;
    fetch(url, { credentials: "include" })
      .then(r => r.blob())
      .then(blob => {
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objUrl;
        const tag = rangePreset === "all" ? "all" : (rangePreset === "custom" ? "custom" : rangePreset);
        a.download = `drivespend-work-trips-${tag}-${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
        URL.revokeObjectURL(objUrl);
      })
      .catch(() => toast.error("Export failed"));
  };

  // Only render for logged-in users
  const filteredTrips = useMemo(() => {
    const { start, end } = computeRange(rangePreset, customFrom, customTo);
    if (!start && !end) return trips;
    return trips.filter(t => {
      if (!t.created_at) return false;
      const ts = new Date(t.created_at).getTime();
      if (start && ts < start.getTime()) return false;
      if (end && ts > end.getTime()) return false;
      return true;
    });
  }, [trips, rangePreset, customFrom, customTo]);

  if (!user) return null;

  const totalDistance = filteredTrips.reduce((s, t) => s + (t.distance_km || 0), 0);
  const totalFuel = filteredTrips.reduce((s, t) => s + (t.fuel_cost || 0), 0);
  const totalReimb = filteredTrips.reduce((s, t) => s + (t.reimbursement_total || 0), 0);
  const totalNet = filteredTrips.reduce((s, t) => s + (t.net || 0), 0);

  return (
    <div className="sf-card mt-8" data-testid="mileage-history-panel" style={{ padding: 28 }}>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#E52020]/10 border border-[#E52020]/20 flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-[#E52020]" />
          </div>
          <div>
            <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#999999]">Work Trip Ledger</div>
            <h2 className="sf-subheading text-lg">Saved Work Trips</h2>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchTrips}
            disabled={loading}
            className="sf-icon-btn"
            data-testid="mileage-refresh-btn"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          {trips.length > 0 && (
            <button
              onClick={handleExportCsv}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold tracking-[0.1em] uppercase text-[#1A1A1A] border border-[#EBE6DC] rounded-full hover:border-[#E52020] hover:text-[#E52020] transition-all"
              data-testid="mileage-export-csv-btn"
            >
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
          )}
        </div>
      </div>

      {trips.length === 0 ? (
        <div className="text-center py-10 text-sm text-[#999999]" data-testid="mileage-empty">
          No work trips saved yet. Calculate a route, then tap <span className="font-semibold text-[#1A1A1A]">Save Work Trip</span> in the work trip calculator panel.
        </div>
      ) : (
        <>
          {/* Filter pills */}
          <div className="flex items-center gap-1.5 mb-4 flex-wrap" data-testid="mileage-filter-pills">
            <Calendar className="w-3.5 h-3.5 text-[#999999] mr-0.5" />
            {RANGE_PRESETS.map(p => (
              <button
                key={p.key}
                onClick={() => setRangePreset(p.key)}
                className={`text-[10px] font-bold tracking-[0.08em] uppercase px-2.5 py-1 rounded-full border transition-all ${
                  rangePreset === p.key
                    ? "bg-[#1A1A1A] border-[#1A1A1A] text-white"
                    : "bg-transparent border-[#EBE6DC] text-[#666666] hover:border-[#1A1A1A] hover:text-[#1A1A1A]"
                }`}
                data-testid={`mileage-filter-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Custom date inputs */}
          {rangePreset === "custom" && (
            <div className="grid grid-cols-2 gap-2 mb-4" data-testid="mileage-filter-custom-inputs">
              <label className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#999999]">
                From
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="mt-1 w-full bg-[#FAFAF7] border border-[#EBE6DC] rounded-xl px-3 py-2 text-sm font-mono text-[#1A1A1A] outline-none focus:border-[#E52020]"
                  data-testid="mileage-filter-from"
                />
              </label>
              <label className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#999999]">
                To
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="mt-1 w-full bg-[#FAFAF7] border border-[#EBE6DC] rounded-xl px-3 py-2 text-sm font-mono text-[#1A1A1A] outline-none focus:border-[#E52020]"
                  data-testid="mileage-filter-to"
                />
              </label>
            </div>
          )}

          {/* Summary row */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <Stat label="Trips" value={filteredTrips.length.toString()} />
            <Stat label="Total km" value={`${totalDistance.toFixed(1)}`} />
          </div>
          <div className="grid grid-cols-3 gap-3 mb-5">
            <Stat label="Fuel cost" value={formatCurrency(totalFuel)} testId="mileage-total-fuel" />
            <Stat label="Reimbursement" value={formatCurrency(totalReimb)} accent testId="mileage-total-reimb" />
            <Stat
              label="Difference"
              value={`${totalNet >= 0 ? "+" : ""}${formatCurrency(totalNet)}`}
              tone={totalNet >= 0 ? "positive" : "negative"}
              testId="mileage-total-difference"
            />
          </div>

          {totalDistance > 14000 && (rangePreset === "fy" || rangePreset === "last_fy") && (
            <div className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800">
              ⚠ You have logged over 14,000 km in this income year. IRD Tier 2 rates apply to the excess.
            </div>
          )}

          {filteredTrips.length === 0 ? (
            <div className="text-center py-8 text-sm text-[#999999]" data-testid="mileage-filtered-empty">
              No trips in this range. Try a different filter.
            </div>
          ) : (
            <>
          {/* Trip list */}
          <div className="space-y-2.5">
            {filteredTrips.map(t => (
              <div key={t.id} className="sf-history-item" data-testid={`mileage-trip-${t.id}`}>
                <div className="flex-1 min-w-0">
                  {/* Primary title = label (editable inline). Click to edit. */}
                  {editingId === t.id ? (
                    <div className="flex items-center gap-1.5" data-testid={`mileage-label-editor-${t.id}`}>
                      <input
                        autoFocus
                        type="text"
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveEdit(t.id); if (e.key === "Escape") cancelEdit(); }}
                        placeholder="Name this trip…"
                        maxLength={48}
                        className="flex-1 min-w-0 bg-white border border-[#E52020] rounded-md px-2 py-1 text-sm font-semibold text-[#1A1A1A] outline-none"
                        data-testid={`mileage-label-edit-input-${t.id}`}
                      />
                      <button onClick={() => saveEdit(t.id)} className="text-[#16A34A] hover:text-[#0E7A3A]" data-testid={`mileage-label-save-${t.id}`} title="Save">
                        <Check className="w-4 h-4" />
                      </button>
                      <button onClick={cancelEdit} className="text-[#999999] hover:text-[#E52020]" title="Cancel">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => startEdit(t)}
                      className="flex items-center gap-2 flex-wrap text-left min-w-0 group"
                      data-testid={`mileage-label-display-${t.id}`}
                      title="Click to rename"
                    >
                      <span className={`font-semibold truncate group-hover:text-[#E52020] transition-colors ${t.label ? "text-[#1A1A1A]" : "text-[#999999] italic"}`}>
                        {t.label || t.trip_name || "Tap to name this trip"}
                      </span>
                      <Tag className="w-3 h-3 text-[#999999] group-hover:text-[#E52020] transition-colors shrink-0" />
                      {t.is_override && (
                        <span className="text-[9px] font-bold tracking-[0.1em] uppercase text-[#E52020] bg-[#E52020]/10 border border-[#E52020]/20 rounded-full px-2 py-0.5">Custom rate</span>
                      )}
                    </button>
                  )}

                  {/* Origin → Destination */}
                  {(t.origin || t.destination) && (
                    <div className="flex items-center gap-1 text-xs text-[#666666] mt-1 truncate">
                      <MapPin className="w-3 h-3 shrink-0 text-[#E52020]" />
                      <span className="truncate">{shortLoc(t.origin)} → {shortLoc(t.destination)}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-3 mt-1.5 text-[11px] text-[#666666] font-mono flex-wrap">
                    <span>{t.distance_km.toFixed(1)} km</span>
                    <span>•</span>
                    <span>${t.reimbursement_rate.toFixed(2)}/km</span>
                    {t.fuel_cost > 0 && (
                      <>
                        <span>•</span>
                        <span data-testid={`mileage-trip-fuel-${t.id}`}>
                          Fuel <span className="text-[#1A1A1A] font-semibold">{formatCurrency(t.fuel_cost)}</span>
                        </span>
                      </>
                    )}
                    <span>•</span>
                    <span>{new Date(t.created_at).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "2-digit" })}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0 ml-3">
                  <div className="text-base font-bold text-[#1A1A1A] font-mono">{formatCurrency(t.reimbursement_total)}</div>
                  <div className={`text-[11px] font-mono ${t.net >= 0 ? "text-[#16A34A]" : "text-[#E52020]"}`}>
                    {t.net >= 0 ? "+" : ""}{formatCurrency(t.net)}
                  </div>
                  <button
                    onClick={() => handleDelete(t.id)}
                    className="mt-1 text-[#999999] hover:text-[#E52020] transition-colors"
                    data-testid={`delete-mileage-${t.id}`}
                    title="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Footer total */}
          <div className="mt-5 pt-4 border-t border-[#EBE6DC] flex items-center justify-between">
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#999999]">Reimbursement − Fuel</span>
            <span className={`text-xl font-bold font-mono ${totalNet >= 0 ? "text-[#16A34A]" : "text-[#E52020]"}`} data-testid="mileage-net-total">
              {totalNet >= 0 ? "+" : ""}{formatCurrency(totalNet)}
            </span>
          </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, accent, tone, testId }) {
  const valueColor =
    tone === "positive" ? "text-[#16A34A]" :
    tone === "negative" ? "text-[#E52020]" :
    accent ? "text-[#E52020]" :
    "text-[#1A1A1A]";
  return (
    <div className="bg-[#FAFAF7] border border-[#EBE6DC] rounded-xl px-3 py-2.5" data-testid={testId}>
      <div className="text-[9px] font-bold tracking-[0.18em] uppercase text-[#999999]">{label}</div>
      <div className={`text-base font-bold mt-0.5 font-mono ${valueColor}`}>{value}</div>
    </div>
  );
}

function shortLoc(s) {
  if (!s) return "—";
  return s.split(",")[0].trim().slice(0, 24);
}
